/**
 * @description 애플리케이션 전용 에러 클래스와 에러 코드를 정의합니다.
 * AppError, AppFormError 클래스와 타입 안전한 에러 컨텍스트를 제공합니다.
 */

import superjson from 'superjson';
import type { Timeframe } from './interface';
import type { Int } from 'ccxt';

/**
 * 애플리케이션 에러 코드
 *
 * 카테고리별로 분류된 에러 코드입니다:
 * - 기존: 일반적인 에러
 * - 거래소: 거래소 연결/설정 관련
 * - 투자/세션: 투자 시작/중지 관련
 * - 포지션/주문: 주문 실행 관련
 * - 잔고: 잔액 관련
 * - 전략: 전략 설정 관련
 * - 인증: 로그인/라이선스 관련
 * - 기타: 텔레그램, 뮤텍스 등
 */
export enum ErrorCode {
  // 기존 (Existing)
  ERR_FORM_INPUT_ERROR = 'ERR_FORM_INPUT_ERROR',
  ERR_POSITION_NOT_FOUND = 'ERR_POSITION_NOT_FOUND',
  ERR_FETCH_CANDLES = 'ERR_FETCH_CANDLES',
  ERR_INSUFFICIENT_PROXIES = 'ERR_INSUFFICIENT_PROXIES',
  ERR_PROXY_INVALID = 'ERR_PROXY_INVALID',

  // 거래소 (Exchange)
  ERR_EXCHANGE_NOT_SELECTED = 'ERR_EXCHANGE_NOT_SELECTED',
  ERR_EXCHANGE_CONFIG_NOT_FOUND = 'ERR_EXCHANGE_CONFIG_NOT_FOUND',
  ERR_EXCHANGE_CONFIG_READ_ERROR = 'ERR_EXCHANGE_CONFIG_READ_ERROR',
  ERR_EXCHANGE_INSTANCE_CREATE_ERROR = 'ERR_EXCHANGE_INSTANCE_CREATE_ERROR',
  ERR_EXCHANGE_CONNECTION_ERROR = 'ERR_EXCHANGE_CONNECTION_ERROR',
  ERR_POSITION_MODE_CHANGE_FAILED = 'ERR_POSITION_MODE_CHANGE_FAILED',

  // 투자/세션 (Invest/Session)
  ERR_INVEST_ALREADY_RUNNING = 'ERR_INVEST_ALREADY_RUNNING',
  ERR_INVEST_ALREADY_STOPPED = 'ERR_INVEST_ALREADY_STOPPED',
  ERR_INVEST_CANNOT_MODIFY_WHILE_RUNNING = 'ERR_INVEST_CANNOT_MODIFY_WHILE_RUNNING',
  ERR_INVEST_NO_SYMBOLS = 'ERR_INVEST_NO_SYMBOLS',
  ERR_INVEST_SYMBOL_CHANGING = 'ERR_INVEST_SYMBOL_CHANGING',
  ERR_INVEST_CANNOT_SWITCH_TAB = 'ERR_INVEST_CANNOT_SWITCH_TAB',
  ERR_INVEST_STOPPING = 'ERR_INVEST_STOPPING',
  ERR_BACKTEST_START_TIME_REQUIRED = 'ERR_BACKTEST_START_TIME_REQUIRED',
  ERR_INVEST_END_TIME_BEFORE_START = 'ERR_INVEST_END_TIME_BEFORE_START',
  ERR_INVEST_END_TIME_IN_PAST = 'ERR_INVEST_END_TIME_IN_PAST',

  // 포지션/주문 (Position/Order)
  ERR_POSITION_NOT_EXISTS = 'ERR_POSITION_NOT_EXISTS',
  ERR_SYMBOL_INFO_NOT_FOUND = 'ERR_SYMBOL_INFO_NOT_FOUND',
  ERR_ENTRY_NOT_ALLOWED = 'ERR_ENTRY_NOT_ALLOWED',
  ERR_IMMEDIATE_LIQUIDATION_RISK = 'ERR_IMMEDIATE_LIQUIDATION_RISK',
  ERR_ORDER_ALREADY_IN_PROGRESS = 'ERR_ORDER_ALREADY_IN_PROGRESS',
  ERR_MARKET_STATUS_NOT_FOUND = 'ERR_MARKET_STATUS_NOT_FOUND',
  ERR_CANDLE_SERVICE_NOT_FOUND = 'ERR_CANDLE_SERVICE_NOT_FOUND',
  ERR_PRICE_NOT_FOUND = 'ERR_PRICE_NOT_FOUND',
  ERR_ORDERBOOK_NOT_FOUND = 'ERR_ORDERBOOK_NOT_FOUND',
  ERR_NO_TRADABLE_AMOUNT = 'ERR_NO_TRADABLE_AMOUNT',
  ERR_VIRTUAL_ORDER_FAILED = 'ERR_VIRTUAL_ORDER_FAILED',
  ERR_POSITION_SIZE_NOT_CHANGED = 'ERR_POSITION_SIZE_NOT_CHANGED',
  ERR_ORDER_CANCELED = 'ERR_ORDER_CANCELED',
  ERR_SYMBOL_AMOUNT_EMPTY = 'ERR_SYMBOL_AMOUNT_EMPTY',

  // 잔고 (Balance)
  ERR_VIRTUAL_BALANCE_TOO_LOW = 'ERR_VIRTUAL_BALANCE_TOO_LOW',
  ERR_INVEST_MODE_MISMATCH = 'ERR_INVEST_MODE_MISMATCH',
  ERR_WITHDRAW_WHILE_STOPPED = 'ERR_WITHDRAW_WHILE_STOPPED',
  ERR_SEED_REQUIRED = 'ERR_SEED_REQUIRED',
  ERR_WITHDRAW_EXCEED_MAX = 'ERR_WITHDRAW_EXCEED_MAX',
  ERR_COST_BELOW_MIN = 'ERR_COST_BELOW_MIN',
  ERR_COST_EXCEED_MAX = 'ERR_COST_EXCEED_MAX',
  ERR_INSUFFICIENT_INVESTABLE = 'ERR_INSUFFICIENT_INVESTABLE',

  // 전략 (Strategy)
  ERR_STRATEGY_CONFIG_READ_ERROR = 'ERR_STRATEGY_CONFIG_READ_ERROR',
  ERR_STRATEGY_LOG_READ_ERROR = 'ERR_STRATEGY_LOG_READ_ERROR',
  ERR_NO_TIMEFRAMES = 'ERR_NO_TIMEFRAMES',
  ERR_UNSUPPORTED_TRIGGER_TYPE = 'ERR_UNSUPPORTED_TRIGGER_TYPE',
  ERR_CONDITION_NOT_FOUND = 'ERR_CONDITION_NOT_FOUND',
  ERR_SIGNAL_TYPE_NOT_FOUND = 'ERR_SIGNAL_TYPE_NOT_FOUND',
  ERR_INDICATOR_NOT_FOUND = 'ERR_INDICATOR_NOT_FOUND',
  ERR_CONDITION_REF_INVALID_INDICATOR = 'ERR_CONDITION_REF_INVALID_INDICATOR',
  ERR_CONDITION_REF_INVALID_SIGNAL_TYPE = 'ERR_CONDITION_REF_INVALID_SIGNAL_TYPE',
  ERR_CONDITION_SYMBOL_REQUIRED = 'ERR_CONDITION_SYMBOL_REQUIRED',
  ERR_CHANNEL_CONDITION_OPERANDS = 'ERR_CHANNEL_CONDITION_OPERANDS',
  ERR_USAGE_MIN_ONE = 'ERR_USAGE_MIN_ONE',
  ERR_NAME_REQUIRED = 'ERR_NAME_REQUIRED',
  ERR_NAME_DUPLICATE = 'ERR_NAME_DUPLICATE',
  ERR_REQUIRED_SIGNAL_TYPE_MODIFY = 'ERR_REQUIRED_SIGNAL_TYPE_MODIFY',
  ERR_REQUIRED_SIGNAL_TYPE_DELETE = 'ERR_REQUIRED_SIGNAL_TYPE_DELETE',
  ERR_STRATEGY_OR_ID_REQUIRED = 'ERR_STRATEGY_OR_ID_REQUIRED',

  //분석
  ERR_CORRELATION_CALC_CANCELLED = 'ERR_CORRELATION_CALC_CANCELLED',

  // 인증 (Auth)
  ERR_ALREADY_LOGGED_IN_OTHER_USER = 'ERR_ALREADY_LOGGED_IN_OTHER_USER',
  ERR_ALREADY_LOGGED_IN_OTHER_LICENSE = 'ERR_ALREADY_LOGGED_IN_OTHER_LICENSE',
  ERR_API_KEY_REQUIRED = 'ERR_API_KEY_REQUIRED',

  // 기타 (Misc)
  ERR_TELEGRAM_API_FAILED = 'ERR_TELEGRAM_API_FAILED',
  ERR_TELEGRAM_NO_CHAT = 'ERR_TELEGRAM_NO_CHAT',
  ERR_TELEGRAM_NO_TOKEN = 'ERR_TELEGRAM_NO_TOKEN',
  ERR_MUTEX_ALREADY_LOCKED = 'ERR_MUTEX_ALREADY_LOCKED',
  ERR_CANDLE_FETCH_MAIN_ONLY = 'ERR_CANDLE_FETCH_MAIN_ONLY',
  ERR_INDICATOR_BUFFER_INSUFFICIENT = 'ERR_INDICATOR_BUFFER_INSUFFICIENT',
  ERR_TALIB_FUNCTION_NOT_FOUND = 'ERR_TALIB_FUNCTION_NOT_FOUND',
  ERR_EXCHANGE_SERVICE_NOT_INIT = 'ERR_EXCHANGE_SERVICE_NOT_INIT',
}

// 각 ErrorCode에 대응하는 context 타입 정의
export type ErrorContextMap = {
  // 기존 (Existing)
  [ErrorCode.ERR_POSITION_NOT_FOUND]: { symbol: string };
  [ErrorCode.ERR_FORM_INPUT_ERROR]: { fieldPath: (string | number)[] };
  [ErrorCode.ERR_FETCH_CANDLES]: {
    symbol: string;
    timeframe: Timeframe;
    since?: Int;
    limit?: Int;
    params?: Record<string, unknown>;
    error?: unknown;
  };
  [ErrorCode.ERR_INSUFFICIENT_PROXIES]: {
    requiredCount: number;
    currentCount: number;
  };
  [ErrorCode.ERR_PROXY_INVALID]: {
    proxyIp: string;
    errorType: 'empty' | 'wrongformat' | 'timeout' | 'connection';
    message?: string;
  };

  // 거래소 (Exchange) - context 없음
  [ErrorCode.ERR_EXCHANGE_NOT_SELECTED]: void;
  [ErrorCode.ERR_EXCHANGE_CONFIG_NOT_FOUND]: void;
  [ErrorCode.ERR_EXCHANGE_CONFIG_READ_ERROR]: void;
  [ErrorCode.ERR_EXCHANGE_INSTANCE_CREATE_ERROR]: void;
  [ErrorCode.ERR_EXCHANGE_CONNECTION_ERROR]: { message?: string };
  [ErrorCode.ERR_POSITION_MODE_CHANGE_FAILED]: { hedgeMode: boolean; message?: string };

  // 투자/세션 (Invest/Session)
  [ErrorCode.ERR_INVEST_ALREADY_RUNNING]: void;
  [ErrorCode.ERR_INVEST_ALREADY_STOPPED]: void;
  [ErrorCode.ERR_INVEST_CANNOT_MODIFY_WHILE_RUNNING]: void;
  [ErrorCode.ERR_INVEST_NO_SYMBOLS]: void;
  [ErrorCode.ERR_INVEST_SYMBOL_CHANGING]: void;
  [ErrorCode.ERR_INVEST_CANNOT_SWITCH_TAB]: void;
  [ErrorCode.ERR_INVEST_STOPPING]: void;
  [ErrorCode.ERR_BACKTEST_START_TIME_REQUIRED]: void;
  [ErrorCode.ERR_INVEST_END_TIME_BEFORE_START]: { startAt: string };
  [ErrorCode.ERR_INVEST_END_TIME_IN_PAST]: void;

  // 포지션/주문 (Position/Order)
  [ErrorCode.ERR_POSITION_NOT_EXISTS]: void;
  [ErrorCode.ERR_SYMBOL_INFO_NOT_FOUND]: void;
  [ErrorCode.ERR_ENTRY_NOT_ALLOWED]: { symbol: string; posOrderType: string };
  [ErrorCode.ERR_IMMEDIATE_LIQUIDATION_RISK]: { liqPrice: number; markPrice: number };
  [ErrorCode.ERR_ORDER_ALREADY_IN_PROGRESS]: { symbol: string };
  [ErrorCode.ERR_MARKET_STATUS_NOT_FOUND]: { symbol?: string };
  [ErrorCode.ERR_CANDLE_SERVICE_NOT_FOUND]: { symbol: string; timeframe: string };
  [ErrorCode.ERR_PRICE_NOT_FOUND]: { symbol: string };
  [ErrorCode.ERR_ORDERBOOK_NOT_FOUND]: void;
  [ErrorCode.ERR_NO_TRADABLE_AMOUNT]: void;
  [ErrorCode.ERR_VIRTUAL_ORDER_FAILED]: { symbol: string };
  [ErrorCode.ERR_POSITION_SIZE_NOT_CHANGED]: { symbol: string };
  [ErrorCode.ERR_ORDER_CANCELED]: { status: string; orderId: number };
  [ErrorCode.ERR_SYMBOL_AMOUNT_EMPTY]: { symbolAmt: number };

  // 잔고 (Balance)
  [ErrorCode.ERR_VIRTUAL_BALANCE_TOO_LOW]: { minBalance: number };
  [ErrorCode.ERR_INVEST_MODE_MISMATCH]: void;
  [ErrorCode.ERR_WITHDRAW_WHILE_STOPPED]: void;
  [ErrorCode.ERR_SEED_REQUIRED]: void;
  [ErrorCode.ERR_WITHDRAW_EXCEED_MAX]: { maxAmount: number };
  [ErrorCode.ERR_COST_BELOW_MIN]: { minCost: number };
  [ErrorCode.ERR_COST_EXCEED_MAX]: { maxCost: number };
  [ErrorCode.ERR_INSUFFICIENT_INVESTABLE]: { investable: number; quoteAmt: number };

  // 전략 (Strategy)
  [ErrorCode.ERR_STRATEGY_CONFIG_READ_ERROR]: void;
  [ErrorCode.ERR_STRATEGY_LOG_READ_ERROR]: void;
  [ErrorCode.ERR_NO_TIMEFRAMES]: void;
  [ErrorCode.ERR_UNSUPPORTED_TRIGGER_TYPE]: { triggerType: string };
  [ErrorCode.ERR_CONDITION_NOT_FOUND]: { conditionId?: number; conditionName?: string };
  [ErrorCode.ERR_SIGNAL_TYPE_NOT_FOUND]: { signalTypeId: number };
  [ErrorCode.ERR_INDICATOR_NOT_FOUND]: { indicatorId: number };
  [ErrorCode.ERR_CONDITION_REF_INVALID_INDICATOR]: { indicatorIds: string };
  [ErrorCode.ERR_CONDITION_REF_INVALID_SIGNAL_TYPE]: { signalTypeIds: string };
  [ErrorCode.ERR_CONDITION_SYMBOL_REQUIRED]: void;
  [ErrorCode.ERR_CHANNEL_CONDITION_OPERANDS]: void;
  [ErrorCode.ERR_USAGE_MIN_ONE]: void;
  [ErrorCode.ERR_NAME_REQUIRED]: void;
  [ErrorCode.ERR_NAME_DUPLICATE]: { name: string };
  [ErrorCode.ERR_REQUIRED_SIGNAL_TYPE_MODIFY]: void;
  [ErrorCode.ERR_REQUIRED_SIGNAL_TYPE_DELETE]: void;
  [ErrorCode.ERR_STRATEGY_OR_ID_REQUIRED]: void;

  //분석
  [ErrorCode.ERR_CORRELATION_CALC_CANCELLED]: void;

  // 인증 (Auth)
  [ErrorCode.ERR_ALREADY_LOGGED_IN_OTHER_USER]: void;
  [ErrorCode.ERR_ALREADY_LOGGED_IN_OTHER_LICENSE]: void;
  [ErrorCode.ERR_API_KEY_REQUIRED]: void;

  // 기타 (Misc)
  [ErrorCode.ERR_TELEGRAM_API_FAILED]: void;
  [ErrorCode.ERR_TELEGRAM_NO_CHAT]: void;
  [ErrorCode.ERR_TELEGRAM_NO_TOKEN]: void;
  [ErrorCode.ERR_MUTEX_ALREADY_LOCKED]: void;
  [ErrorCode.ERR_CANDLE_FETCH_MAIN_ONLY]: void;
  [ErrorCode.ERR_INDICATOR_BUFFER_INSUFFICIENT]: void;
  [ErrorCode.ERR_TALIB_FUNCTION_NOT_FOUND]: { funcId: number };
  [ErrorCode.ERR_EXCHANGE_SERVICE_NOT_INIT]: void;
};

// code에 따라 ctx 타입이 결정되는 에러 컨텍스트
export type ErrorContext<T extends ErrorCode = ErrorCode> = ErrorContextMap[T];

// context가 필수인지 선택적인지 결정하는 조건부 타입
// 빈 객체({})가 해당 타입에 할당 가능하면 선택적 (모든 속성이 optional이거나 void)
type ErrorContextParam<T extends ErrorCode> = {} extends ErrorContextMap[T]
  ? [ctx?: ErrorContextMap[T]]
  : [ctx: ErrorContextMap[T]];

/**
 * 애플리케이션 전용 에러 클래스
 *
 * 타입 안전한 에러 코드와 컨텍스트를 지원합니다.
 * SuperJSON을 통해 프로세스 간 직렬화가 가능합니다.
 *
 * @typeParam T - 에러 코드 타입
 *
 * @example
 * ```ts
 * throw new AppError(ErrorCode.ERR_POSITION_NOT_FOUND, { symbol: 'BTC/USDT' });
 * ```
 */
export class AppError<T extends ErrorCode = ErrorCode> extends Error {
  /** 에러 코드 */
  public readonly code: T;
  /** 에러 컨텍스트 (에러 코드별로 타입이 결정됨) */
  public readonly ctx: ErrorContext<T>;

  constructor(code: T, ...args: ErrorContextParam<T>) {
    super(code); // fallback으로 code를 message로 사용
    this.name = 'AppError';
    this.code = code;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    this.ctx = (args[0] ?? {}) as ErrorContext<T>;
  }

  // JSON 직렬화 시 code와 context를 포함하도록 설정
  toJSON() {
    return {
      name: this.name,
      code: this.code,
      ctx: this.ctx,
    };
  }
}

/**
 * 에러가 AppError 인스턴스인지 확인하는 타입 가드
 *
 * @param error - 검사할 에러 객체
 * @returns AppError이면 true
 */
export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError && error.code !== undefined;
}

/**
 * 특정 ErrorCode를 가진 AppError인지 확인하는 타입 가드 함수
 * 이 함수를 사용하면 TypeScript가 context의 정확한 타입을 추론할 수 있습니다.
 *
 * @example
 * if (isAppErrorOfCode(e, ErrorCode.ERR_FORM_INPUT_ERROR)) {
 *   // 이제 e.ctx.fieldPath가 정확한 타입으로 추론됩니다
 *   console.log(e.ctx.fieldPath); // (string | number)[]
 * }
 */
export function isAppErrorOfCode<TCode extends ErrorCode>(error: unknown, code: TCode): error is AppError<TCode> {
  return error instanceof AppError && error.code === code;
}

/**
 * SuperJSON에 AppError를 등록합니다.
 * Worker 프로세스 간 통신에서 AppError의 커스텀 속성(ctx)을 보존하기 위해 필요합니다.
 *
 * 이 함수는 애플리케이션 시작 시 Main 프로세스와 Worker 프로세스 모두에서 호출되어야 합니다.
 */
export function registerSuperJsonError() {
  // AppError, AppFormError 클래스를 SuperJSON에 등록
  // 이를 통해 instanceof 체크가 올바르게 작동하고 클래스가 복원됩니다
  superjson.registerClass(AppError);
  superjson.registerClass(AppFormError);

  // 커스텀 속성 'code'와 'ctx'를 직렬화하도록 허용
  // 기본적으로 에러의 커스텀 속성은 보안상 생략되므로 명시적으로 허용해야 합니다
  superjson.allowErrorProps('code', 'ctx');
}

// ============================================================================
// AppFormError - 폼 입력 유효성 검사 에러
// ============================================================================

/**
 * 폼 입력 에러 코드
 * strategy-utils.ts 등에서 폼 유효성 검사 시 사용
 */
export enum FormErrorCode {
  // 신호 관련
  SIGNAL_NOT_SELECTED = 'SIGNAL_NOT_SELECTED',
  SIGNAL_NO_CONDITIONS = 'SIGNAL_NO_CONDITIONS',
  SIGNAL_DUPLICATE = 'SIGNAL_DUPLICATE',

  // 수량/비율 관련
  AMOUNT_INVALID = 'AMOUNT_INVALID',
  AMOUNT_RATIO_INVALID = 'AMOUNT_RATIO_INVALID',
  ENTER_AMOUNT_SUM_NOT_100 = 'ENTER_AMOUNT_SUM_NOT_100',
  EXIT_LAST_AMOUNT_NOT_100 = 'EXIT_LAST_AMOUNT_NOT_100',
  LAST_AMOUNT_NOT_100 = 'LAST_AMOUNT_NOT_100',

  // 추적 청산 관련
  TRAILING_CHANGE_INVALID = 'TRAILING_CHANGE_INVALID',

  // 구간 청산 관련
  EXCLUSIVE_CHANGE_INVALID = 'EXCLUSIVE_CHANGE_INVALID',
  STOPLOSS_REQUIRED = 'STOPLOSS_REQUIRED',
  STOPLOSS_FIRST_AMOUNT_NOT_100 = 'STOPLOSS_FIRST_AMOUNT_NOT_100',
  TAKEPROFIT_LAST_AMOUNT_NOT_100 = 'TAKEPROFIT_LAST_AMOUNT_NOT_100',

  // 단계 청산 관련
  OVERLAPPING_VALUE_INVALID = 'OVERLAPPING_VALUE_INVALID',
  OVERLAPPING_AMOUNT_INVALID = 'OVERLAPPING_AMOUNT_INVALID',
  OVERLAPPING_AMOUNT_EXCEED = 'OVERLAPPING_AMOUNT_EXCEED',
  OVERLAPPING_UP_GREATER_THAN_DOWN = 'OVERLAPPING_UP_GREATER_THAN_DOWN',
  OVERLAPPING_DOWN_ORDER_INVALID = 'OVERLAPPING_DOWN_ORDER_INVALID',
  OVERLAPPING_UP_ORDER_INVALID = 'OVERLAPPING_UP_ORDER_INVALID',
  OVERLAPPING_FIRST_DOWN_AMOUNT_NOT_100 = 'OVERLAPPING_FIRST_DOWN_AMOUNT_NOT_100',
  OVERLAPPING_LAST_UP_AMOUNT_NOT_100 = 'OVERLAPPING_LAST_UP_AMOUNT_NOT_100',

  // 조건 관련
  CONDITION_ID_DUPLICATE = 'CONDITION_ID_DUPLICATE',

  // 지표 관련
  INDICATOR_TIMEFRAME_REQUIRED = 'INDICATOR_TIMEFRAME_REQUIRED',
}

/**
 * 폼 에러 컨텍스트 기본 타입 (fieldPath 필수)
 */
type FormErrorContextBase = {
  fieldPath: (string | number)[];
};

/**
 * 각 FormErrorCode에 대응하는 context 타입 정의
 * 추가 컨텍스트가 필요한 코드만 명시, 나머지는 기본 타입 사용
 */
export type FormErrorContextMap = {
  [FormErrorCode.SIGNAL_NO_CONDITIONS]: FormErrorContextBase & { signalTypeName?: string };
  [FormErrorCode.ENTER_AMOUNT_SUM_NOT_100]: FormErrorContextBase & { triggerType?: string };
  [FormErrorCode.EXCLUSIVE_CHANGE_INVALID]: FormErrorContextBase & { triggerType?: string };
  [FormErrorCode.CONDITION_ID_DUPLICATE]: FormErrorContextBase & { conditionId?: number };
};

/**
 * FormErrorCode에 따른 context 타입 결정
 * FormErrorContextMap에 정의되지 않은 코드는 기본 타입 사용
 */
export type FormErrorContext<T extends FormErrorCode = FormErrorCode> = T extends keyof FormErrorContextMap
  ? FormErrorContextMap[T]
  : FormErrorContextBase;

/**
 * 폼 입력 유효성 검사 전용 에러 클래스
 *
 * fieldPath를 통해 어떤 필드에서 에러가 발생했는지 표시합니다.
 * 전략 설정 폼 등에서 입력 검증 시 사용됩니다.
 *
 * @typeParam T - 폼 에러 코드 타입
 *
 * @example
 * ```ts
 * throw new AppFormError(
 *   FormErrorCode.AMOUNT_INVALID,
 *   { fieldPath: ['triggers', 'signal_enter', 'long', 'steps', 0, 'amount'] }
 * );
 * ```
 */
export class AppFormError<T extends FormErrorCode = FormErrorCode> extends Error {
  /** 폼 에러 코드 */
  public readonly code: T;
  /** 에러 컨텍스트 (fieldPath 포함) */
  public readonly ctx: FormErrorContext<T>;

  constructor(code: T, ctx: FormErrorContext<T>) {
    super(code);
    this.name = 'AppFormError';
    this.code = code;
    this.ctx = ctx;
  }

  toJSON() {
    return {
      name: this.name,
      code: this.code,
      ctx: this.ctx,
    };
  }
}

/**
 * 에러가 AppFormError 인스턴스인지 확인하는 타입 가드
 *
 * @param error - 검사할 에러 객체
 * @returns AppFormError이면 true
 */
export function isAppFormError(error: unknown): error is AppFormError {
  return error instanceof AppFormError && error.code !== undefined;
}

/**
 * 특정 FormErrorCode를 가진 AppFormError인지 확인하는 타입 가드
 *
 * @typeParam TCode - 확인할 폼 에러 코드
 * @param error - 검사할 에러 객체
 * @param code - 확인할 폼 에러 코드
 * @returns 해당 코드의 AppFormError이면 true
 */
export function isAppFormErrorOfCode<TCode extends FormErrorCode>(
  error: unknown,
  code: TCode,
): error is AppFormError<TCode> {
  return error instanceof AppFormError && error.code === code;
}

/**
 * instanceof 를 매번 체크 후 message 반환하는 코드가 너무 많아서 이 함수를 생성함.
 * @param e
 * @returns
 */
export function getErrMessage(e: unknown): string {
  if (e instanceof Error) {
    return e.message;
  }
  if (e instanceof Object) {
    return e.toString();
  }
  return String(e);
}

/**
 * instanceof 를 매번 체크 후 stack 반환하는 코드가 너무 많아서 이 함수를 생성함.
 * @param e
 * @returns
 */
export function getErrStack(e: unknown): string | undefined {
  if (e instanceof Error) {
    return e.stack;
  }
  return undefined;
}
